import type { EnhancedManCodeId } from '#api/enhancements/enhancedTmsApiTypes'
import { appRoutes } from '#app/navigation/routes/routes'
import type { OptionChangeId } from '#app/store/builders/configChangeTracker'
import type { LocalConfigurationId, TruckConfiguration } from '#app/store/slices/configurationSlice'
import type { ConfigurationStage } from '#app/types/Configuration'
import type { CheckTaskId } from '#app/types/ConfigurationCheck'

/*
 * These location creators follow the same structure:
 * - all routes per step are laid out explicitly
 * - all sub views (like History, Comparison) have creators that take a configuration
 *   - if a view cannot be reached, the generalized creator returns undefined (like Print in GeneralStep for example)
 * - a sub view generally only wants to be reached by one URL (Step-3 Result View is the default view, it does not get a `step-3/result` URL)
 * - all URIs are included and exported (primarily to use the without typos in the ConfigurationRouter)
 */

export const makeManCodeWithSortOrder = (configuration: TruckConfiguration | undefined): ManCodeWithSortOrder | undefined =>
  configuration?.apiConfiguration?.manCode ? { manCode: configuration.apiConfiguration.manCode, sortOrder: configuration.sortOrder } : undefined
export const selectConfigurationUri = (configuration: TruckConfiguration): EnhancedManCodeId | LocalConfigurationId =>
  configuration.apiConfiguration.manCode ?? selectConfigurationUri(configuration)

export interface ManCodeWithSortOrder {
  manCode: EnhancedManCodeId
  sortOrder: number
}

/*
 * Step 0 Locations
 */
// here is no routerUri location, because this is the index page
export const makeConfigurationCreateLocation = () => appRoutes['configuration/new'].make({})

export const makeConfigurationStep0LoadingLocation = (configuration: LocalConfigurationId, manCode: ManCodeWithSortOrder | undefined) =>
  appRoutes['configuration/step-0'].make({ configuration, manCode })

export const makeConfigurationStepRetryLocation = (configuration: TruckConfiguration | undefined) => {
  if (!configuration) {
    return undefined
  }
  const configurationStep = configuration.step
  if (!configurationStep.error) {
    return undefined
  }
  const requestedConfigurationStep = configurationStep.requested
  switch (requestedConfigurationStep) {
    case 'GeneralStep':
      return appRoutes['configuration/step-1'].make({ configuration })
    case 'DetailedStep':
      return appRoutes['configuration/step-2'].make({ configuration })
  }

  return undefined
}

export const makeConfigurationStepErrorLocation = (configuration: TruckConfiguration) => appRoutes['configuration/error'].make({ configuration })

/**
 * Special case: passing `configurationId` as undefined will create a new configuration, otherwise it will navigate to the loading state
 */
export const makeConfigurationStep0DefaultLocation = (
  configurationId: LocalConfigurationId | undefined,
  manCode: ManCodeWithSortOrder | undefined,
) => (configurationId ? makeConfigurationStep0LoadingLocation(configurationId, manCode) : makeConfigurationCreateLocation())

export const makeConfigurationStep0HistoryLocation = (
  configuration: LocalConfigurationId,
  manCode: ManCodeWithSortOrder | undefined,
  processId?: OptionChangeId,
) => appRoutes['configuration/step-0/history'].make({ configuration, manCode, processId })

export const makeConfigurationStep0LandingLocation = (
  configuration: LocalConfigurationId | TruckConfiguration,
  manCode: ManCodeWithSortOrder | undefined,
) => appRoutes['configuration/step-0/landing'].make({ configuration, manCode })

/*
 * Step 1 Locations
 */
export const makeConfigurationStep1DefaultLocation = (configuration: LocalConfigurationId, manCode: ManCodeWithSortOrder | undefined) =>
  appRoutes['configuration/step-1'].make({ configuration, manCode })

export const makeConfigurationStep1HistoryLocation = (
  configuration: LocalConfigurationId,
  manCode: ManCodeWithSortOrder | undefined,
  processId?: OptionChangeId,
) => appRoutes['configuration/step-1/history'].make({ configuration, manCode, processId })

/*
 * Step 2 Locations
 */
export const makeConfigurationStep2DefaultLocation = (configuration: LocalConfigurationId, manCode: ManCodeWithSortOrder | undefined) =>
  appRoutes['configuration/step-2'].make({ configuration, manCode })

export const makeConfigurationStep2HistoryLocation = (
  configuration: LocalConfigurationId,
  manCode: ManCodeWithSortOrder | undefined,
  processId?: OptionChangeId,
) => appRoutes['configuration/step-2/history'].make({ configuration, manCode, processId })

export function isCompareLocation(
  location: AnyConfigurationLocation | undefined,
): location is ReturnType<typeof makeConfigurationStep2ComparisonLocation> | ReturnType<typeof makeConfigurationStep3ComparisonLocation> {
  return location?.id.endsWith('compare') ?? false
}

export const makeConfigurationStep2ComparisonLocation = (
  baseConfigurationId: LocalConfigurationId,
  baseManCodeId: ManCodeWithSortOrder | undefined,
  comparisonConfigurationId?: LocalConfigurationId,
  comparisonManCodeId?: ManCodeWithSortOrder,
) =>
  appRoutes['configuration/step-2/compare'].make({
    configuration: baseConfigurationId,
    manCode: baseManCodeId,
    otherConfiguration: comparisonConfigurationId,
    otherConfigurationManCode: comparisonManCodeId,
  })

export const makeConfigurationStep2PrintLocation = (configuration: LocalConfigurationId, manCode: ManCodeWithSortOrder | undefined) =>
  appRoutes['configuration/step-2/print'].make({ configuration, manCode })

export function hasTaskIdLocation(
  location: AnyConfigurationLocation | undefined,
): location is
  | ReturnType<typeof makeConfigurationStep2TechnicalDataLocation>
  | ReturnType<typeof makeConfigurationStep2BuildVerificationLocation>
  | ReturnType<typeof makeConfigurationStep3TechnicalDataLocation>
  | ReturnType<typeof makeConfigurationStep3BuildVerificationLocation> {
  return (location?.id.endsWith('technical-data') || location?.id.endsWith('build-verification')) ?? false
}

export const makeConfigurationStep2TechnicalDataLocation = (configuration: LocalConfigurationId, manCode: ManCodeWithSortOrder | undefined) =>
  appRoutes['configuration/step-2/technical-data'].make({ configuration, manCode })

export const makeConfigurationStep2BuildVerificationLocation = (
  configuration: LocalConfigurationId,
  manCode: ManCodeWithSortOrder | undefined,
  taskId?: CheckTaskId,
) => appRoutes['configuration/step-2/build-verification'].make({ configuration, manCode, taskId })

export const makeConfigurationStep2OptionalEquipmentLocation = (configuration: LocalConfigurationId, manCode: ManCodeWithSortOrder | undefined) =>
  appRoutes['configuration/step-2/optional-equipment'].make({ configuration, manCode })

/*
 * Step 3 Locations
 */
export const makeConfigurationStep3DefaultLocation = (configuration: LocalConfigurationId, manCode: ManCodeWithSortOrder | undefined) =>
  appRoutes['configuration/step-3'].make({ configuration, manCode })

export const makeConfigurationStep3SubmittedLocation = (configuration: LocalConfigurationId, manCode: ManCodeWithSortOrder | undefined) =>
  appRoutes['configuration/step-3/submitted'].make({ configuration, manCode })

export const makeConfigurationStep3ChangesLocation = (configuration: LocalConfigurationId, manCode: ManCodeWithSortOrder | undefined) =>
  appRoutes['configuration/step-3/changes'].make({ configuration, manCode })

export const makeConfigurationStep3TechnicalDataLocation = (configuration: LocalConfigurationId, manCode: ManCodeWithSortOrder | undefined) =>
  appRoutes['configuration/step-3/technical-data'].make({ configuration, manCode })

export const makeConfigurationStep3BuildVerificationLocation = (
  configuration: LocalConfigurationId,
  manCode: ManCodeWithSortOrder | undefined,
  taskId?: CheckTaskId,
) => appRoutes['configuration/step-3/build-verification'].make({ configuration, manCode, taskId })

export const makeConfigurationStep3ComparisonLocation = (
  baseConfigurationId: LocalConfigurationId,
  baseManCodeId: ManCodeWithSortOrder | undefined,
  comparisonConfigurationId?: LocalConfigurationId,
  comparisonManCodeId?: ManCodeWithSortOrder,
) =>
  appRoutes['configuration/step-3/compare'].make({
    configuration: baseConfigurationId,
    manCode: baseManCodeId,
    otherConfiguration: comparisonConfigurationId,
    otherManCode: comparisonManCodeId,
  })

export const makeConfigurationStep3OptionalEquipmentLocation = (configuration: LocalConfigurationId, manCode: ManCodeWithSortOrder | undefined) =>
  appRoutes['configuration/step-3/optional-equipment'].make({ configuration, manCode })

/*
 * Copy Location
 */
export const makeCopyConfigurationLocationRaw = (configuration: LocalConfigurationId, manCode: ManCodeWithSortOrder | undefined) =>
  appRoutes['configuration/copy'].make({ configuration, manCode })
export const makeCopyConfigurationLocation = (configuration?: TruckConfiguration) => {
  return configuration ? makeCopyConfigurationLocationRaw(configuration.id, makeManCodeWithSortOrder(configuration)) : undefined
}

/*
 * Fallback Location Renderers: Default Location (primary view of that)
 */
export const makeDefaultConfigurationLocationRaw = (
  configuration: LocalConfigurationId,
  manCode: ManCodeWithSortOrder | undefined,
  stage: ConfigurationStage,
) => {
  switch (stage) {
    case 'SummaryStep':
      return appRoutes['configuration/step-3'].make({ configuration, manCode })
    case 'DetailedStep':
      return appRoutes['configuration/step-2'].make({ configuration, manCode })
    case 'GeneralStep':
      return appRoutes['configuration/step-1'].make({ configuration, manCode })
    case 'PendingStep':
      return appRoutes['configuration/step-0'].make({ configuration, manCode })
  }
}
export const makeDefaultConfigurationLocation = (configuration: TruckConfiguration | undefined) =>
  configuration ? makeDefaultConfigurationLocationRaw(configuration.id, makeManCodeWithSortOrder(configuration), configuration.step.stage) : undefined

export const makeConfigurationLocation = (configuration?: TruckConfiguration | undefined): AnyConfigurationLocation => {
  if (!configuration) {
    return undefined
  }

  if (configuration.step.error || configuration.creation.error) {
    return makeConfigurationStepErrorLocation(configuration)
  }

  return makeLocationFromViewId(configuration, configuration?.step.view) ?? makeDefaultConfigurationLocation(configuration)
}

/*
 * Fallback Location Renderers: History Location
 */
export const makeHistoryConfigurationLocationRaw = (
  configurationId: LocalConfigurationId,
  manCode: ManCodeWithSortOrder | undefined | undefined,
  step: ConfigurationStage,
  process?: OptionChangeId,
) => {
  switch (step) {
    case 'PendingStep':
      return appRoutes['configuration/step-0/history'].make({ configuration: configurationId, manCode, processId: process })
    case 'GeneralStep':
      return appRoutes['configuration/step-1/history'].make({ configuration: configurationId, manCode, processId: process })
    case 'DetailedStep':
      return appRoutes['configuration/step-2/history'].make({ configuration: configurationId, manCode, processId: process })
    case 'SummaryStep':
      return appRoutes['configuration/step-3/history'].make({ configuration: configurationId, manCode, processId: process })
  }
}
export const makeHistoryConfigurationLocation = (configuration: TruckConfiguration | undefined, process?: OptionChangeId) =>
  configuration
    ? makeHistoryConfigurationLocationRaw(configuration.id, makeManCodeWithSortOrder(configuration), configuration.step.stage, process)
    : undefined

/*
 * Fallback Location Renderers: Comparison Location
 */
export const makeComparisonConfigurationLocationRaw = (
  baseId: LocalConfigurationId,
  baseManCodeId: ManCodeWithSortOrder | undefined,
  baseStep: ConfigurationStage,
  comparisonId?: LocalConfigurationId,
  comparisonManCodeId?: ManCodeWithSortOrder,
) => {
  switch (baseStep) {
    case 'SummaryStep':
    case 'DetailedStep':
      return appRoutes['configuration/step-2/compare'].make({
        configuration: baseId,
        manCode: baseManCodeId,
        otherConfiguration: comparisonId,
        otherConfigurationManCode: comparisonManCodeId,
      })
    case 'GeneralStep':
    case 'PendingStep':
      return undefined
  }
}
export const makeComparisonConfigurationLocation = (base: TruckConfiguration, comparison?: TruckConfiguration) =>
  base
    ? makeComparisonConfigurationLocationRaw(
        base.id,
        makeManCodeWithSortOrder(base),
        base.step.stage,
        comparison?.id,
        makeManCodeWithSortOrder(comparison),
      )
    : undefined

/*
 * Fallback Location Renderers: Print Location
 */
export const makePrintConfigurationLocationRaw = (
  configurationId: LocalConfigurationId,
  manCode: ManCodeWithSortOrder | undefined,
  stage: ConfigurationStage,
) => {
  switch (stage) {
    case 'DetailedStep':
      return appRoutes['configuration/step-2/print'].make({ configuration: configurationId, manCode })
    case 'SummaryStep':
      return appRoutes['configuration/step-3'].make({ configuration: configurationId, manCode })
    case 'GeneralStep':
    case 'PendingStep':
      return undefined
  }
}
export const makePrintConfigurationLocation = (configuration?: TruckConfiguration) =>
  configuration ? makePrintConfigurationLocationRaw(configuration.id, makeManCodeWithSortOrder(configuration), configuration.step.stage) : undefined

/*
 * Fallback Location Renderers: Build Verification Location / Technical Data
 */
export const makeBuildVerificationLocation = (configuration?: TruckConfiguration, taskId?: CheckTaskId) =>
  configuration
    ? makeBuildVerificationLocationRaw(configuration.id, makeManCodeWithSortOrder(configuration), configuration.step.stage, taskId)
    : undefined

export const makeTechnicalDataLocationRaw = (
  configurationId: LocalConfigurationId,
  manCode: ManCodeWithSortOrder | undefined,
  stage: ConfigurationStage,
) => {
  switch (stage) {
    case 'DetailedStep':
      return appRoutes['configuration/step-2/technical-data'].make({ configuration: configurationId, manCode })
    case 'SummaryStep':
      return appRoutes['configuration/step-3/technical-data'].make({ configuration: configurationId, manCode })
    case 'GeneralStep':
    case 'PendingStep':
      return undefined
  }
}

/*
 * Fallback Location Renderers: Build Verification Location
 */
export const makeBuildVerificationLocationRaw = (
  configurationId: LocalConfigurationId,
  manCode: ManCodeWithSortOrder | undefined,
  stage: ConfigurationStage,
  taskId?: CheckTaskId,
) => {
  switch (stage) {
    case 'DetailedStep':
      return appRoutes['configuration/step-2/build-verification'].make({ configuration: configurationId, manCode, taskId })
    case 'SummaryStep':
      return appRoutes['configuration/step-3/build-verification'].make({ configuration: configurationId, manCode, taskId })
    case 'GeneralStep':
    case 'PendingStep':
      return undefined
  }
}
export const makeTechnicalDataLocation = (configuration?: TruckConfiguration) =>
  configuration ? makeTechnicalDataLocationRaw(configuration.id, makeManCodeWithSortOrder(configuration), configuration.step.stage) : undefined

export const makeOptionalEquipmentLocationRaw = (
  configuration: LocalConfigurationId,
  manCode: ManCodeWithSortOrder | undefined,
  stage: ConfigurationStage,
) => {
  switch (stage) {
    case 'DetailedStep':
      return appRoutes['configuration/step-2/optional-equipment'].make({ configuration, manCode })
    case 'SummaryStep':
      return appRoutes['configuration/step-3/optional-equipment'].make({ configuration, manCode })
    case 'GeneralStep':
    case 'PendingStep':
      return undefined
  }
}

export const makeOptionalEquipmentLocation = (configuration?: TruckConfiguration) =>
  configuration ? makeOptionalEquipmentLocationRaw(configuration.id, makeManCodeWithSortOrder(configuration), configuration.step.stage) : undefined

export const makeTireEditorLocationRaw = (
  configuration: LocalConfigurationId,
  manCode: ManCodeWithSortOrder | undefined,
  stage: ConfigurationStage,
) => {
  switch (stage) {
    case 'DetailedStep':
      return appRoutes['configuration/step-2/tire-editor'].make({ configuration, manCode })
    case 'PendingStep':
    case 'GeneralStep':
    case 'SummaryStep':
      return undefined
  }
}

export const makeTireEditorLocation = (configuration?: TruckConfiguration) =>
  configuration ? makeTireEditorLocationRaw(configuration.id, makeManCodeWithSortOrder(configuration), configuration.step.stage) : undefined

export type AnyConfigurationLocation =
  // create is a special router, only exists there
  | ReturnType<typeof makeConfigurationStep0DefaultLocation>
  | ReturnType<typeof makeConfigurationStep0LandingLocation>
  | ReturnType<typeof makeDefaultConfigurationLocation>
  | ReturnType<typeof makeHistoryConfigurationLocation>
  | ReturnType<typeof makeComparisonConfigurationLocation>
  | ReturnType<typeof makeConfigurationStep3ComparisonLocation>
  | ReturnType<typeof makeCopyConfigurationLocation>
  | ReturnType<typeof makePrintConfigurationLocation>
  | ReturnType<typeof makeBuildVerificationLocation>
  | ReturnType<typeof makeTechnicalDataLocation>
  | ReturnType<typeof makeBuildVerificationLocationRaw>
  | ReturnType<typeof makeOptionalEquipmentLocation>
  | ReturnType<typeof makeTireEditorLocationRaw>
  // changes is the only view that really only exists in Step3
  | ReturnType<typeof makeConfigurationStep3DefaultLocation>
  | ReturnType<typeof makeConfigurationStep3ChangesLocation>
  | ReturnType<typeof makeConfigurationStep3SubmittedLocation>
  // error handlers
  | ReturnType<typeof makeConfigurationStepErrorLocation>

export type AnyConfigurationViewId = Exclude<AnyConfigurationLocation, undefined>['id']

export const makeLocationFromViewId = (
  configuration: TruckConfiguration | undefined,
  viewId: AnyConfigurationViewId | undefined,
): AnyConfigurationLocation => {
  if (viewId === undefined) {
    return undefined
  }
  if (viewId === 'configuration/copy') {
    return makeCopyConfigurationLocation(configuration)
  }
  if (!configuration) {
    return undefined
  }

  switch (viewId) {
    case 'configuration/step-0':
      return makeConfigurationStep0DefaultLocation(configuration?.id, makeManCodeWithSortOrder(configuration))
    case 'configuration/step-0/landing':
      return makeConfigurationStep0LandingLocation(configuration?.id, makeManCodeWithSortOrder(configuration))
    case 'configuration/step-0/history':
      return makeConfigurationStep0HistoryLocation(configuration?.id, makeManCodeWithSortOrder(configuration))
    case 'configuration/step-1':
      return makeConfigurationStep1DefaultLocation(configuration.id, makeManCodeWithSortOrder(configuration))
    case 'configuration/step-1/history':
      return makeConfigurationStep1HistoryLocation(configuration.id, makeManCodeWithSortOrder(configuration))
    case 'configuration/step-2':
      return makeConfigurationStep2DefaultLocation(configuration.id, makeManCodeWithSortOrder(configuration))
    case 'configuration/step-2/history':
      return makeConfigurationStep2HistoryLocation(configuration.id, makeManCodeWithSortOrder(configuration))
    case 'configuration/step-2/compare':
      return makeConfigurationStep2ComparisonLocation(
        configuration.id,
        makeManCodeWithSortOrder(configuration),
        configuration.step.otherConfigurationId,
        configuration.step.otherConfigurationManCode,
      )
    case 'configuration/step-2/print':
      return makeConfigurationStep2PrintLocation(configuration.id, makeManCodeWithSortOrder(configuration))
    case 'configuration/step-2/technical-data':
      return makeConfigurationStep2TechnicalDataLocation(configuration.id, makeManCodeWithSortOrder(configuration))
    case 'configuration/step-2/build-verification':
      return makeConfigurationStep2BuildVerificationLocation(configuration.id, makeManCodeWithSortOrder(configuration))
    case 'configuration/step-3':
      return makeConfigurationStep3DefaultLocation(configuration.id, makeManCodeWithSortOrder(configuration))
    case 'configuration/step-3/compare':
      return makeConfigurationStep3ComparisonLocation(
        configuration.id,
        makeManCodeWithSortOrder(configuration),
        configuration.step.otherConfigurationId,
        configuration.step.otherConfigurationManCode,
      )
    case 'configuration/step-3/technical-data':
      return makeConfigurationStep3TechnicalDataLocation(configuration.id, makeManCodeWithSortOrder(configuration))
    case 'configuration/step-3/build-verification':
      return makeConfigurationStep3BuildVerificationLocation(configuration.id, makeManCodeWithSortOrder(configuration))
    case 'configuration/step-3/changes':
      return makeConfigurationStep3ChangesLocation(configuration.id, makeManCodeWithSortOrder(configuration))
    case 'configuration/step-3/submitted':
      return makeConfigurationStep3SubmittedLocation(configuration.id, makeManCodeWithSortOrder(configuration))
    case 'configuration/error':
      return makeConfigurationStepErrorLocation(configuration)
  }
}
